import { lazy, type ComponentType } from 'react'
import { Navigate, Route, Routes } from 'react-router'

import { useSession } from './auth/context'
import { Layout } from './components/AppShell'
import { AccountClosedPage, DeleteAccountPage } from './pages/DeleteAccountPage'
import { ChangeBusinessTypePage } from './pages/ChangeBusinessTypePage'
import { ChangePasswordPage, ForgotPasswordPage, LoginPage, ReopenAccountPage, SignupPage } from './pages/AuthPages'
import { LegalPage } from './pages/LegalPages'
import { SubscriptionPage } from './pages/SubscriptionPage' // also used by the frame (the banner), so it stays in the main bundle
import { t } from './strings'

/**
 * The pages after sign-in load when they are opened, so that the sign-in page and the first paint do not carry the Dashboard,
 * the reports, the settings and the rest. The frame (Layout) shows a short "Memuat" while one arrives.
 */
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })))

const DashboardPage = page(() => import('./pages/DashboardPage'), 'DashboardPage')
const HomePage = page(() => import('./pages/ReportPages'), 'HomePage')
const SalesPage = page(() => import('./pages/ReportPages'), 'SalesPage')
const SaleDetailPage = page(() => import('./pages/ReportPages'), 'SaleDetailPage')
const PayrollPage = page(() => import('./pages/PayrollPage'), 'PayrollPage')
const DataPage = page(() => import('./pages/DataPages'), 'DataPage')
const EmenuPage = page(() => import('./pages/EmenuPage'), 'EmenuPage')
const DevicesPage = page(() => import('./pages/AppPages'), 'DevicesPage')
const SettingsPage = page(() => import('./pages/AppPages'), 'SettingsPage')
const KasirSettingsPage = page(() => import('./pages/KasirSettingsPage'), 'KasirSettingsPage')
const ReceiptLayoutPage = page(() => import('./pages/ReceiptLayoutPage'), 'ReceiptLayoutPage')
const UsersPage = page(() => import('./pages/UsersPage'), 'UsersPage')
const PasswordPage = page(() => import('./pages/ProfileCards'), 'PasswordPage')
const HistoryPage = page(() => import('./pages/HistoryPage'), 'HistoryPage')

export function App() {
  const { status } = useSession()
  if (status === 'loading') return <p className="center">{t.loading}</p>
  if (status === 'must_change_password') return <ChangePasswordPage />
  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/masuk" element={<LoginPage />} />
        <Route path="/akun-ditutup" element={<AccountClosedPage />} />
        <Route path="/daftar" element={<SignupPage />} />
        <Route path="/lupa-kata-sandi" element={<ForgotPasswordPage />} />
        <Route path="/buka-kembali" element={<ReopenAccountPage />} />
        <Route path="/:doc" element={<LegalPage />} />
        <Route path="*" element={<Navigate to="/masuk" replace />} />
      </Routes>
    )
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<DashboardPage />} />
        <Route path="laporan" element={<HomePage />} />
        <Route path="laporan/komisi" element={<PayrollPage />} />
        <Route path="dashboard" element={<Navigate to="/" replace />} />
        <Route path="transaksi" element={<SalesPage />} />
        <Route path="transaksi/:id" element={<SaleDetailPage />} />
        <Route path="data" element={<DataPage />} />
        <Route path="hp-kasir" element={<DevicesPage />} />
        <Route path="pengaturan" element={<SettingsPage />} />
        <Route path="pengaturan/kasir" element={<KasirSettingsPage />} />
        <Route path="pengaturan/e-menu" element={<EmenuPage />} />
        <Route path="pengaturan/struk" element={<ReceiptLayoutPage />} />
        <Route path="pengaturan/pengguna" element={<UsersPage />} />
        <Route path="pengaturan/kata-sandi" element={<PasswordPage />} />
        <Route path="pengaturan/riwayat" element={<HistoryPage />} />
        <Route path="pengaturan/hapus-akun" element={<DeleteAccountPage />} />
        <Route path="pengaturan/jenis-usaha" element={<ChangeBusinessTypePage />} />
        <Route path="langganan" element={<SubscriptionPage />} />
      </Route>
      <Route path="/akun-ditutup" element={<AccountClosedPage />} />
      <Route path="/:doc" element={<LegalPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
